import { useEffect, useMemo, useState } from 'react';
import { DefaultOptionType } from 'antd/lib/select';
import { useDebounce } from 'react-use';

// Models
import { useIntl } from 'react-intl';
import { PersonType } from '../../models/enums/PersonType';

// Components
import { useData } from '../App/useData';
import { ApiEndpoints } from '../../data/ApiEndpoints';
import { SelectedOption } from '../../models/SelectedOption';
import { OptionsProviderProps, SelectMode } from '../../models/OptionsProviderProps';
import { PassTemplate } from '../../models/PassTemplate';

// Hook
export const usePassTemplateSelect = (
  defaultValue?: Array<SelectedOption>,
  personType?: PersonType,
  mode: SelectMode = undefined
) => {
  // Intl
  const intl = useIntl();

  // State
  const [selected, setSelected] = useState<Array<SelectedOption>>(defaultValue ?? []);
  const [options, setOptions] = useState<DefaultOptionType[]>([]);
  const [pending, setPending] = useState(true);
  const [search, setSearch] = useState('');

  // Data
  const { data: passTemplates, fetch: fetchData } = useData(ApiEndpoints.passTemplates.list, null);

  const locales = useMemo(
    () => ({
      placeholder: intl.formatMessage({ id: 'printCard.select.passTemplate.placeholder' }),
      description: intl.formatMessage({ id: 'visits.form.guests.description' }),
      empty: intl.formatMessage({ id: 'passTemplate.form.empty' }),
    }),
    [intl]
  );

  useEffect(() => {
    fetchData({ sortedBy: 'Name' });
  }, [fetchData]);

  const filteredOptions = useMemo(() => {
    if (!passTemplates) return [];

    let filtered = passTemplates.filter((x) => {
      if (personType === PersonType.Visitor) return x.EnabledForVisitors;
      if (personType === PersonType.Employee) return x.EnabledForEmployees;
      return true;
    });

    if (search) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter((x) => x.Name.toLowerCase().includes(lowerSearch));
    }

    return filtered.map((passTemplate) => ({
      label: passTemplate.Name,
      value: passTemplate.Id,
    }));
  }, [passTemplates, personType, search]);

  useEffect(() => {
    setOptions(filteredOptions);
    setPending(false);
  }, [filteredOptions]);

  useDebounce(
    () => {
      setPending(true);
      setOptions(filteredOptions);
      setPending(false);
    },
    300,
    [search]
  );

  // Hook response
  return useMemo<OptionsProviderProps<PassTemplate>>(
    () => ({
      data: passTemplates,
      selected,
      options,
      search,
      mode,
      pending,
      locales,
      setSelected,
      setSearch,
    }),
    [passTemplates, selected, options, search, mode, pending, locales, setSelected, setSearch]
  );
};
